import { useState } from 'react';
import type { MemberLevel, OrgMember, UnitCatalogue } from '@keetcheck/shared';
import { theme } from '../theme.js';
import { LEVELS, LEVEL_LABEL } from '../lib/unit.js';
import { useUpdateMemberUnit, useSetMemberQualifications, useSetMemberPositions } from '../lib/queries.js';

type QualDraft = { grade: string | null; expiresAt: string | null };

/**
 * Admin editor for one member's unit details: operational / UDO / level,
 * qualifications (grade, expiry) and positions. One Save sends only what changed.
 */
export function MemberUnitEditor({ orgId, member, catalogue, onDone }: {
  orgId: string; member: OrgMember; catalogue: UnitCatalogue; onDone: () => void;
}) {
  const unitMut = useUpdateMemberUnit(orgId);
  const qualMut = useSetMemberQualifications(orgId);
  const posMut = useSetMemberPositions(orgId);

  const [operational, setOperational] = useState(member.operational);
  const [udo, setUdo] = useState(member.udo);
  const [level, setLevel] = useState<MemberLevel | null>(member.level);
  const [quals, setQuals] = useState<Map<string, QualDraft>>(
    () => new Map(member.qualifications.map((q) => [q.typeId, { grade: q.grade, expiresAt: q.expiresAt }])),
  );
  const [positions, setPositions] = useState<Set<string>>(() => new Set(member.positionIds));
  const [error, setError] = useState<string | null>(null);

  // Retired types still show if this member holds them, so they can be removed.
  const qualTypes = catalogue.qualifications.filter((t) => t.active || quals.has(t.id));
  const positionTypes = catalogue.positions.filter((p) => p.active || positions.has(p.id));

  function toggleQual(typeId: string, grades: string[] | null) {
    setQuals((prev) => {
      const next = new Map(prev);
      if (next.has(typeId)) next.delete(typeId);
      else next.set(typeId, { grade: grades?.[0] ?? null, expiresAt: null });
      return next;
    });
  }

  function patchQual(typeId: string, patch: Partial<QualDraft>) {
    setQuals((prev) => new Map(prev).set(typeId, { ...prev.get(typeId)!, ...patch }));
  }

  function togglePosition(id: string) {
    setPositions((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  const busy = unitMut.isPending || qualMut.isPending || posMut.isPending;

  async function save() {
    setError(null);
    const qualList = [...quals].map(([typeId, d]) => ({ typeId, grade: d.grade, expiresAt: d.expiresAt || null }));
    const sameQuals = JSON.stringify(qualList.map((q) => [q.typeId, q.grade, q.expiresAt]).sort())
      === JSON.stringify(member.qualifications.map((q) => [q.typeId, q.grade, q.expiresAt]).sort());
    const samePositions = [...positions].sort().join() === [...member.positionIds].sort().join();
    const unitChanged = operational !== member.operational || udo !== member.udo || level !== member.level;
    try {
      if (unitChanged) await unitMut.mutateAsync({ userId: member.userId, operational, udo, level });
      if (!sameQuals) await qualMut.mutateAsync({ userId: member.userId, qualifications: qualList });
      if (!samePositions) await posMut.mutateAsync({ userId: member.userId, positionIds: [...positions] });
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save');
    }
  }

  return (
    <div style={{ padding: '12px 18px 16px', background: theme.surface2, borderBottom: `1px solid ${theme.border}`, fontFamily: theme.font }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', marginBottom: 14 }}>
        <label style={checkLabel}>
          <input type="checkbox" checked={operational} onChange={(e) => setOperational(e.target.checked)} />
          Operational
        </label>
        <label style={checkLabel}>
          <input type="checkbox" checked={udo} onChange={(e) => setUdo(e.target.checked)} />
          On UDO roster
        </label>
        <label style={{ ...checkLabel, gap: 8 }}>
          Level
          <select value={level ?? ''} onChange={(e) => setLevel((e.target.value || null) as MemberLevel | null)} style={{ ...input, width: 150 }}>
            <option value="">Not set</option>
            {LEVELS.map((l) => <option key={l} value={l}>{LEVEL_LABEL[l]}</option>)}
          </select>
        </label>
      </div>
      {!operational && (
        <div style={{ fontSize: 12, color: theme.textMuted, marginTop: -8, marginBottom: 12 }}>
          Office only: not on duty crew or ops support. Can still be a UDO.
        </div>
      )}

      <div style={sectionTitle}>Qualifications</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
        {qualTypes.map((t) => {
          const held = quals.get(t.id);
          return (
            <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minHeight: 32 }}>
              <label style={{ ...checkLabel, flex: '1 1 200px', minWidth: 0 }}>
                <input type="checkbox" checked={!!held} onChange={() => toggleQual(t.id, t.grades)} />
                <strong style={{ width: 38, flexShrink: 0 }}>{t.code}</strong>
                <span style={{ color: theme.textDim, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {t.name}{!t.active && ' (retired)'}
                </span>
              </label>
              {held && t.grades && (
                <select value={held.grade ?? ''} onChange={(e) => patchQual(t.id, { grade: e.target.value || null })}
                  aria-label={`${t.code} grade`} style={{ ...input, width: 130 }}>
                  {t.grades.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
              )}
              {held && t.hasExpiry && (
                <label style={{ ...checkLabel, gap: 6, fontSize: 12, color: theme.textMuted }}>
                  expires
                  <input type="date" value={held.expiresAt ?? ''} onChange={(e) => patchQual(t.id, { expiresAt: e.target.value || null })}
                    aria-label={`${t.code} expiry date`} style={{ ...input, width: 150 }} />
                </label>
              )}
            </div>
          );
        })}
      </div>

      <div style={sectionTitle}>Positions</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
        {positionTypes.map((p) => {
          const on = positions.has(p.id);
          return (
            <button key={p.id} type="button" onClick={() => togglePosition(p.id)} aria-pressed={on} style={{
              padding: '5px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              fontFamily: theme.font,
              border: `1px solid ${on ? theme.accent : theme.border}`,
              background: on ? theme.accent : theme.surface,
              color: on ? theme.accentInk : theme.textDim,
            }}>
              {p.name}{!p.active && ' (retired)'}
            </button>
          );
        })}
      </div>

      {error && <div style={{ fontSize: 12, color: theme.status.danger.fg, marginBottom: 10 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 10 }}>
        <button type="button" onClick={onDone} disabled={busy} style={cancelBtn}>Cancel</button>
        <button type="button" onClick={() => void save()} disabled={busy} style={saveBtn}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

const checkLabel: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: theme.text, cursor: 'pointer',
};

const sectionTitle: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase',
  color: theme.textMuted, marginBottom: 8,
};

const input: React.CSSProperties = {
  padding: '6px 8px', background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 8,
  fontSize: 13, color: theme.text, fontFamily: theme.font, outline: 'none', boxSizing: 'border-box',
};

const cancelBtn: React.CSSProperties = {
  flex: 1, padding: '9px 0', borderRadius: 10, background: theme.surface, border: `1px solid ${theme.border}`,
  color: theme.textDim, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: theme.font,
};

const saveBtn: React.CSSProperties = {
  flex: 2, padding: '9px 0', borderRadius: 10, background: theme.accent, border: 'none',
  color: theme.accentInk, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: theme.font,
};
